/**
 * @file MovementsFilters.tsx
 * @description Строка фильтров журнала движений (phase19.24 этап 1, шаг 2).
 *
 * Одна строка на весь экран (чек-лист §10.3): поиск → селекты справа. Свой
 * диапазон дат разворачивается второй строкой и только при выборе «Свой
 * период» — два пустых поля даты в шапке мешали бы в 90% случаев.
 *
 * Поиск клиентский по загруженному окну журнала, поэтому подпись поля честно
 * называет, что ищем: товар, артикул, номер партии или документа (отдельного
 * поля «Партия» с phase19.31 нет — SC59).
 *
 * phase19.31: даты периода — общее поле даты кабинета `DateField` (маска и
 * календарь) вместо нативного; высоты — из темы.
 */

import { RotateCcw } from "lucide-react";

import type { StockFilters } from "./use-stock-filters";
import { Button } from "../../ui/button";
import { FilterBar } from "../../ui/FilterBar";
import { DateField } from "../../ui/DateField";
import { SearchInput } from "../../ui/SearchInput";
import { PHONE_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import { SelectField } from "../../ui/SelectField";
import { MOVEMENTS_PERIOD_OPTIONS } from "../../../lib/movements-period";
import { MOVEMENT_OPERATION_OPTIONS } from "../../../lib/movements-texts";
import type { WarehouseDto } from "../../../lib/api-wms";

interface Props {
  filters: StockFilters;
  onChange: (patch: Partial<StockFilters>) => void;
  onReset: () => void;
  dirty: boolean;
  warehouses: WarehouseDto[];
}

/** Поле даты диапазона — подпись слева. */
function PeriodDate({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex min-w-0 flex-1 items-center gap-2 text-sm text-on-surface-variant sm:flex-none">
      {label}
      <DateField value={value} onChange={onChange} ariaLabel={`Период ${label}`} title={`Период ${label}`} />
    </label>
  );
}

export function MovementsFilters({
  filters,
  onChange,
  onReset,
  dirty,
  warehouses,
}: Props) {
  const phone = useMediaQuery(PHONE_QUERY);
  return (
    <div className="flex flex-col gap-3">
      <FilterBar
        search={
          // Партия — в том же поиске (SC59): ссылка «Движения по партии»
          // приходит с номером в поиске, и человек видит, чем отобран журнал.
          // На телефоне подсказка короче — длинная обрезалась на 360.
          <SearchInput
            value={filters.search}
            onChange={(search) => onChange({ search })}
            placeholder={phone ? "Товар, партия или документ" : "Товар, артикул, партия или документ"}
            ariaLabel="Поиск по движениям: товар, артикул, номер партии или документа"
          />
        }
      >
        <SelectField
          value={filters.operation}
          onChange={(operation) =>
            onChange({ operation: operation as StockFilters["operation"] })
          }
          options={MOVEMENT_OPERATION_OPTIONS.map((item) => ({
            value: item.value,
            label: item.label,
          }))}
          placeholder="Все операции"
          ariaLabel="Операция"
          className="w-full sm:w-[200px]"
        />
        <SelectField
          value={filters.period}
          onChange={(period) => onChange({ period: period as StockFilters["period"] })}
          options={MOVEMENTS_PERIOD_OPTIONS}
          placeholder="30 дней"
          ariaLabel="Период"
          className="w-full sm:w-[170px]"
        />
        <SelectField
          value={filters.warehouseId}
          onChange={(warehouseId) => onChange({ warehouseId })}
          options={[
            { value: "", label: "Все склады" },
            ...warehouses.map((w) => ({ value: w.id, label: w.name })),
          ]}
          placeholder="Все склады"
          ariaLabel="Склад"
          className="w-full sm:w-[220px]"
        />
        {dirty ? (
          <Button
            type="button"
            variant="outline"
            onClick={onReset}
            className="w-full sm:w-auto"
          >
            <RotateCcw className="size-4" />
            <span className="ml-1.5">Сбросить</span>
          </Button>
        ) : null}
      </FilterBar>

      {filters.period === "custom" ? (
        <div className="flex flex-wrap items-center gap-3">
          <PeriodDate
            label="с"
            value={filters.from}
            onChange={(from) => onChange({ from })}
          />
          <PeriodDate label="по" value={filters.to} onChange={(to) => onChange({ to })} />
        </div>
      ) : null}
    </div>
  );
}
